import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ALL_PROPERTIES } from '../../graphql/queries';

/**
 * Property filter component for Work Order Reports
 * Allows filtering work orders by specific properties
 */
function WorkOrderPropertyFilter({
  selectedProperties,
  onChange,
  className = ''
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch all properties (no date filtering needed)
  // Use high limit to get all properties for the dropdown
  const { data: propertiesData, loading, error } = useQuery(GET_ALL_PROPERTIES, {
    variables: {
      limit: 1000  // Get all properties for filter dropdown
    },
    fetchPolicy: 'cache-first'
  });

  // Extract and sort properties
  const properties = useMemo(() => {
    const props = propertiesData?.allProperties?.items || [];
    return props
      .filter(p => p.isActive)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [propertiesData]);

  // Filter properties based on search term
  const filteredProperties = useMemo(() => {
    const trimmedSearch = (searchTerm || '').trim();
    if (!trimmedSearch) return properties;
    
    const search = trimmedSearch.toLowerCase();
    
    // Split search into words for more flexible matching
    const searchWords = search.split(/\s+/).filter(word => word.length > 0);
    
    return properties.filter(p => {
      // Combine all searchable fields into one string
      const searchableText = [
        p.name || '',
        p.propertyCode || '',
        p.area || '',
        p.address || '',
        p.city || ''
      ].join(' ').toLowerCase();
      
      // Match if ANY search word is found in the searchable text
      return searchWords.some(word => searchableText.includes(word));
    });
  }, [properties, searchTerm]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePropertyToggle = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    if (!property) return;

    const isSelected = selectedProperties.some(p => p.id === propertyId);
    
    if (isSelected) {
      onChange(selectedProperties.filter(p => p.id !== propertyId));
    } else {
      onChange([...selectedProperties, { id: property.id, name: property.name, propertyCode: property.propertyCode }]);
    }
  };

  const handleSelectAll = () => {
    if (selectedProperties.length === filteredProperties.length) {
      onChange([]);
    } else {
      onChange(filteredProperties.map(p => ({ id: p.id, name: p.name, propertyCode: p.propertyCode })));
    }
  };

  const handleClearAll = () => {
    onChange([]);
    setSearchTerm('');
  };

  const isPropertySelected = (propertyId) => {
    return selectedProperties.some(p => p.id === propertyId);
  };

  return (
    <div className={className} ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <span className="flex items-center">
          <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Property Filter
        </span>
      </label>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center text-sm text-gray-500 py-2">
          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading properties...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-red-50 p-3 border border-red-200">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="ml-2 text-sm text-red-700">
              Failed to load properties. Please try again.
            </p>
          </div>
        </div>
      )}

      {/* Main Filter UI */}
      {!loading && !error && (
        <div className="space-y-2">
          {/* Dropdown Trigger */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center justify-between w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <span className="text-gray-700">
                {selectedProperties.length === 0 
                  ? 'All properties (no filter)' 
                  : `${selectedProperties.length} propert${selectedProperties.length !== 1 ? 'ies' : 'y'} selected`
                }
              </span>
              <svg className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                {/* Search Input */}
                <div className="p-2 border-b border-gray-100">
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search properties..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Quick actions */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                  >
                    {selectedProperties.length === filteredProperties.length && filteredProperties.length > 0 
                      ? 'Deselect All' 
                      : `Select All${searchTerm ? ' Filtered' : ''} (${filteredProperties.length})`
                    }
                  </button>
                  {selectedProperties.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Property list */}
                <div className="max-h-60 overflow-y-auto">
                  {filteredProperties.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">
                      {searchTerm 
                        ? `No properties match "${searchTerm}" (${properties.length} total properties)` 
                        : 'No properties available'
                      }
                    </div>
                  ) : (
                    <div className="py-1">
                      {filteredProperties.map((property) => (
                        <label
                          key={property.id}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isPropertySelected(property.id)}
                            onChange={() => handlePropertyToggle(property.id)}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <div className="ml-2 flex-1 min-w-0">
                            <span className="text-sm text-gray-700 block truncate">
                              {property.name}
                            </span>
                            <span className="text-xs text-gray-500 block truncate">
                              {property.propertyCode && `${property.propertyCode} • `}
                              {property.area || 'No area'}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Selected properties tags */}
          {selectedProperties.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {selectedProperties.slice(0, 3).map((property) => (
                <span
                  key={property.id}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                >
                  {(property.name || '').length > 20 
                    ? (property.name || '').substring(0, 20) + '...'
                    : property.name
                  }
                  <button
                    type="button"
                    onClick={() => handlePropertyToggle(property.id)}
                    className="ml-1 text-purple-600 hover:text-purple-800"
                    aria-label={`Remove ${property.name}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              ))}
              {selectedProperties.length > 3 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  +{selectedProperties.length - 3} more
                </span>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-1">
            Filter work orders by specific properties ({properties.length} available)
          </p>
        </div>
      )}
    </div>
  );
}

export default WorkOrderPropertyFilter;

